import {Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {AnswerFolderModel} from '../../models/api/answerFolderModel';
import {AppState} from '../../store/appState';
import {getAnswerFolders} from '../../store/data/answerFolders/answerFolderSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';

/*
 * Props.
 */

interface MoveAnswerDialogToplineContainerProps {
  folderId?: number;
  onFolderClick: HandlerOf<AnswerFolderModel | undefined>;
}

/*
 * Selectors.
 */

function getFolderId(_state: AppState, props: MoveAnswerDialogToplineContainerProps) {
  return props.folderId;
}

function makeGetParentFolders() {
  return createSelector([getFolderId, getAnswerFolders], (folderId, answerFolders) => {
    if (!folderId) {
      return undefined;
    }

    const folder = answerFolders[folderId];
    if (!folder) {
      return undefined;
    }

    return compact([...folder.folderPath.map((parent) => answerFolders[parent]), folder]);
  });
}

/*
 * Component.
 */

const MoveAnswerDialogToplineContainerComponent: FC<
  MoveAnswerDialogToplineContainerProps &
    MoveAnswerDialogToplineContainerProps &
    ResolveStateProps<typeof mapStateToProps>
> = (props) => {
  const theme = useUserTheme();
  const {parentFolders} = props;
  const folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb> = (parentFolders || []).map(
    (parentFolder) => ({
      label: (
        <>
          <Icon color={theme.greys.shade70} name="folderSmall" />
          {parentFolder.name}
        </>
      ),
      onBreadcrumbClick: () => props.onFolderClick(parentFolder),
    }),
  );

  return (
    <SettingsBreadcrumbs
      topLevelBreadcrumb={{
        label: <Trans id="settings-allfolders">All</Trans>,
        onBreadcrumbClick: () => props.onFolderClick(undefined),
      }}
      breadcrumbs={folderPathBreadcrumbs}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MoveAnswerDialogToplineContainerProps>()(() => ({
  parentFolders: makeGetParentFolders(),
}));
export const MoveAnswerDialogToplineContainer = connect(mapStateToProps)(
  MoveAnswerDialogToplineContainerComponent,
);
